/*
 * Author: Harper Yan
 * File Created: Thursday, 24-07-18 09:51 pm
 * Last Modified: Tuesday, 25-03-11 05:59 pm, by Ben Zhao
 */
import type { CustomAxiosRequestConfig } from '@web/http';
import { clearCache, get, post, put } from '@web/http';
import { connectUrl } from '@web/utils';

import type { FiatTransaction } from '@/@types/asset';
import type {
  ApplyReason,
  ATMPasswordParams,
  CardFee,
  CardInfo,
  CardSensitivity,
  CardTransactionDetail,
  CardTransactionsReq,
  CardTransactionsRes,
  CardTransactionSummary,
  EligibilityCheck,
  FiatAccountInfo,
  LookUpPostCodeResponse,
  MyCard,
  PhysicalCardApplyInfo,
  TransactionCard,
} from '@/@types/card';
import type {
  PointDetailRequest,
  UserPointDetails,
  UserPointSummary,
} from '@/@types/depoint';

export const getCardList = () => {
  return get<CardInfo[]>('/gateway/card/private/v1/card-list');
};

export const getMyCardDetail = (cardId: string) => {
  return get<MyCard>('/gateway/card/private/v1/card-detail', { cardId });
};

export const getMyCardSecrets = (cardId: string) => {
  return get<CardSensitivity>(
    '/gateway/card/private/v1/card-secrets',
    { cardId },
    {
      headers: {
        'x-mfa-purpose': 'CARD_SECRETS',
      },
    },
  );
};

// todo
export const freezeCard = (cardId: string) => {
  return put<boolean>('/gateway/card/private/v1/card-frozen', { cardId });
};

// todo
export const unfreezeCard = (cardId: string) => {
  return put<boolean>(
    '/gateway/card/private/v1/card-unfrozen',
    { cardId },
    {
      headers: {
        'x-mfa-purpose': 'CARD_UNFROZEN',
      },
    },
  );
};

export const activatePhysicalCard = (cardId: string) => {
  return put<boolean>(
    '/gateway/card/private/v1/physical-card-active',
    {
      cardId,
    },
    {
      headers: {
        'x-mfa-purpose': 'PHYSICAL_CARD_ACTIVE',
      },
    },
  );
};

export const getCardNameOptions = (cardId: string) => {
  return get<string[]>('/gateway/card/private/v1/physical-card-name-process', {
    cardId,
  });
};

// todo
export const getCardFee = (categoryId: string, country?: string) => {
  return post<CardFee>('/gateway/card/private/v1/card-fee', {
    categoryId,
    pickupCountry: country,
  });
};

// todo
export const checkPriviledge = (
  cardId: string,
  categoryId: string,
  reason: ApplyReason,
) => {
  return post<EligibilityCheck>('/gateway/card/private/v1/eligibility-check', {
    cardId,
    categoryId,
    reason,
  });
};

export const checkReferalCode = (code: string) => {
  return post<boolean>('/gateway/card/private/v1/referral-code-check', {
    referralCode: code,
  });
};

export const lookupPostcode = (code: string) => {
  return post<LookUpPostCodeResponse | null>(
    '/gateway/kyc/private/v1/get-postalcode-address',
    {
      postalCode: code,
    },
  );
};

export const applyForPhysicalCard = (data: PhysicalCardApplyInfo) => {
  const { cardType: _, ...resData } = data;

  return post<boolean>(
    '/gateway/card/private/v1/physical-card-apply',
    resData,
    {
      headers: {
        'x-mfa-purpose': 'PHYSICAL_CARD_APPLY',
      },
    },
  );
};

export const getPhysicalApplicableCategories = (cardId: string) => {
  // /gateway/card/private/v1/bill-pdf-download?cardId=1104&billMonth=2024-09
  return get<CardInfo[]>(
    connectUrl('/gateway/card/private/v1/physical-appliable-categories', {
      cardId,
    }),
  );
};

// todo
export const setATMPassword = (_data: ATMPasswordParams) => {
  return post<ATMPasswordParams>(
    '/gateway/card/private/v1/card-atm-pin-set',
    _data,
    {
      headers: {
        'x-mfa-purpose': 'CARD_ATM_PIN_SET',
      },
    },
  );
};

export const applyNewCard = (categoryId: string) => {
  return post<string>(`/gateway/card/private/v1/application`, { categoryId });
};

export const getUserPointSummary = () => {
  return get<UserPointSummary>(
    '/gateway/marketing/private/v1/get-user-points-summary',
  );
};

export const postUserPointDetails = (_data: PointDetailRequest) => {
  return post<UserPointDetails>(
    '/gateway/marketing/private/v1/get-user-points-details',
    _data,
  );
};

export const postTransactionList = (_data: CardTransactionsReq) => {
  return get<CardTransactionsRes>(
    '/gateway/card/private/v1/transactions',
    _data,
  );
};

export const getCardBillPdfList = (_params: { cardId: string }) => {
  // /gateway/card/private/v1/bill-pdf-list?cardId=1104
  return get<string[]>(`/gateway/card/private/v1/bill-pdf-list`, _params);
};

export const getCardBillPdfDownload = (_params: {
  cardId: string;
  billMonth: string;
}) => {
  // /gateway/card/private/v1/bill-pdf-download?cardId=1104&billMonth=2024-09
  return get<string>(`/gateway/card/private/v1/bill-pdf-download`, _params);
};

export const getCardTransactionSummary = (_params: CardTransactionsReq) => {
  return get<CardTransactionSummary[]>(
    `/gateway/card/private/v1/statement`,
    _params,
  );
};

export const getCartTransactionDateRange = (_params: { cardId?: string }) => {
  return get<{ start: string; end: string } | null>(
    '/gateway/card/private/v1/statement/range',
    _params,
  );
};

export const getCardTransactionDetail = (_params: {
  page: number;
  rows: number;
  statementId: string;
  cardId?: string;
}) => {
  return get<CardTransactionDetail[]>(
    `/gateway/card/private/v1/statement/detail`,
    _params,
  );
};

export const getUserTransactionCardList = async () => {
  return get<TransactionCard[]>(`/gateway/card/private/v1/all-cards`);
};

export const getFiatDepositAccount = async (
  config?: CustomAxiosRequestConfig,
) => {
  return post<FiatAccountInfo>(
    `/gateway/card/private/v1/fiat/deposit-account`,
    {},
    config,
  );
};

const FIAT_TRANSACTIONS_URL = `/gateway/card/private/v1/fiat/transactions`;

export const clearFiatTransactionsCache = () => {
  clearCache(FIAT_TRANSACTIONS_URL);
};

export const getFiatTransactions = async (params: {
  page: number;
  rows: number;
  force?: boolean;
}) => {
  const { force, ...resParams } = params;

  return (
    (
      await get<FiatTransaction[] | undefined>(
        FIAT_TRANSACTIONS_URL,
        resParams,
        {
          cacheConfig: {
            useCache: !force,
          },
        },
      )
    ).data || []
  );
};

export const getCardBalance = async (currency = 'SGD') => {
  return (
    await get<{ cardFreeAmount: string }>(
      `/gateway/card/private/v1/card-balance`,
      { currency },
    )
  ).data.cardFreeAmount;
};
